import { type ChangeEvent, useCallback } from "react";
import { debounce } from "throttle-debounce";
import type {
  ClassifierValue,
  ClassPostObjType,
} from "../../../services/api/instances/classificatorApi/types";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { deepEqual } from "../../../shared/utils/deepEqualObject";
import { updateStorageObjectAction } from "../redux/actions/actions";
import {
  selectClassifiers,
  selectSelectedClassifier,
  selectSelectedClassifierObjectVersion,
} from "../redux/selectors";
import {
  setClassifiersAction,
  setSelectedClassifierAction,
} from "../redux/slice";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export const useClassifier = () => {
  const dispatch = useAppDispatch();
  const classifiers = useAppSelector(selectClassifiers);
  const selectedClassifier = useAppSelector(selectSelectedClassifier);
  const selectedVersion = useAppSelector(selectSelectedClassifierObjectVersion);

  const storage = createStorageObjectApi<ClassifierValue>(classifiers);

  const classifierUpdate = useCallback(
    debounce(1000, (postObj: StorageObject<ClassifierValue>) => {
      dispatch(updateStorageObjectAction(postObj));
    }),
    [],
  );

  const handleInputFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: keyof ClassPostObjType,
  ) => {
    const updatedClassifierData: ClassPostObjType = {
      ...selectedClassifier?.value?.classifier_object,
      [field]: event.target.value,
    } as ClassPostObjType;
    const updatedClassifier = {
      ...selectedClassifier,
      value: {
        classifier_object: updatedClassifierData,
      },
    };
    const updatedClassifiers = storage.updateById(
      selectedClassifier?.id as string,
      {
        value: {
          classifier_object: updatedClassifierData,
        },
      },
      true,
    );

    dispatch(
      setSelectedClassifierAction(
        updatedClassifier as StorageObject<ClassifierValue>,
      ),
    );
    dispatch(
      setClassifiersAction(
        updatedClassifiers as StorageObject<ClassifierValue>[],
      ),
    );
    classifierUpdate(updatedClassifier as StorageObject<ClassifierValue>);
  };

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedClassifier = {
      ...selectedClassifier,
      friendly_name: event.target.value,
    };
    const updatedClassifiersList = storage.updateById(
      updatedClassifier?.id as string,
      {
        friendly_name: event.target.value,
        children: updatedClassifier?.children,
      },
      true,
    );
    classifierUpdate(updatedClassifier as StorageObject<ClassifierValue>);
    dispatch(
      setClassifiersAction(
        updatedClassifiersList as StorageObject<ClassifierValue>[],
      ),
    );
    dispatch(
      setSelectedClassifierAction(
        updatedClassifier as StorageObject<ClassifierValue>,
      ),
    );
  };

  const compareClassiFierValue = (): boolean => {
    const classifierValue = selectedClassifier?.value?.classifier_object;
    const versionValue = selectedVersion?.value?.classifier_object;
    const strictCompareKeys: Array<keyof ClassPostObjType> = [
      "prefix",
      "text_request",
    ];

    const deepCompareKeys: Array<keyof ClassPostObjType> = ["labels"];

    for (const key of strictCompareKeys) {
      if (classifierValue?.[key] !== versionValue?.[key]) return false;
    }

    for (const key of deepCompareKeys) {
      if (!deepEqual(classifierValue?.[key], versionValue?.[key])) return false;
    }

    return true;
  };

  return {
    storage,
    handleInputFieldChange,
    onNameChange,
    compareClassiFierValue,
  };
};
